import * as d3 from 'd3';
import { ComputationGraph } from '../../graph/types';
import { ComponentDef } from '../../components/types';
import type { EmbeddingStep } from './embedding-detailed';
import type { ResidualStep } from './residual-connection-detailed';

export type RenderFn = (
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  params: Record<string, number | string>,
  meta: ComponentDef,
  graph: ComputationGraph
) => { width: number; height: number };

export interface StepInfo {
  id: number;
  label: string;
  shortLabel: string;
  formula: string;
  explanation: string;
  code: { title: string; lines: string[] };
  svgGroup: string;
  color: string;
  fill: string;
}

export type RichRenderFn = (
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  params: Record<string, number | string>,
  meta: ComponentDef,
  graph: ComputationGraph
) => { width: number; height: number; steps: StepInfo[] };

// All components use the rich 3-column layout
const richLoaders: Record<string, () => Promise<RichRenderFn>> = {
  // Bespoke embedding renderer (draws actual matrix + 3D tensor)
  embedding: () =>
    import('./embedding-detailed').then((m) => m.renderEmbeddingDetailed as unknown as RichRenderFn),

  // Custom hand-crafted renderers (return steps: [])
  attention: () => import('./attention-detailed').then((m) => m.renderAttentionDetailed as unknown as RichRenderFn),
  mlp: () => import('./mlp').then((m) => m.renderMlpDetailed as unknown as RichRenderFn),
  'transformer-block': () =>
    import('./transformer-block').then((m) => m.renderTransformerBlockDetailed as unknown as RichRenderFn),

  // Core config-based renderers
  norm: () => import('./norm-detailed').then((m) => m.renderNormDetailed as unknown as RichRenderFn),
  'moe-layer': () => import('./moe-layer-detailed').then((m) => m.renderMoeLayerDetailed as unknown as RichRenderFn),
  'residual-connection': () =>
    import('./residual-connection-detailed').then((m) => m.renderResidualConnectionDetailed as unknown as RichRenderFn),
  'lm-head': () => import('./lm-head-detailed').then((m) => m.renderLmHeadDetailed as unknown as RichRenderFn),
  'positional-encoding': () => import('./positional-encoding-detailed').then((m) => m.renderPositionalEncodingDetailed),
  'architecture-comparison': () => import('./architecture-comparison-detailed').then((m) => m.renderArchitectureComparisonDetailed as unknown as RichRenderFn),
  'rag-architecture': () => import('./rag-architecture-detailed').then((m) => m.renderRagArchitectureDetailed as unknown as RichRenderFn),

  // Bespoke function renderers
  matmul: () => import('./matmul-detailed').then((m) => m.renderMatmulDetailed as unknown as RichRenderFn),

  // Bespoke softmax renderer
  softmax: () => import('./softmax-detailed').then((m) => m.renderSoftmaxDetailed as unknown as RichRenderFn),

  // Bespoke cross-entropy renderer
  'cross-entropy': () => import('./cross-entropy-detailed').then((m) => m.renderCrossEntropyDetailed as unknown as RichRenderFn),
  'activation-functions': () => import('./activation-functions-detailed').then((m) => m.renderActivationFunctionsDetailed as unknown as RichRenderFn),
  tokenizer: () => import('./tokenizer-detailed').then((m) => m.renderTokenizerDetailed as unknown as RichRenderFn),
  'decoding-strategies': () =>
    import('./decoding-strategies-detailed').then((m) => m.renderDecodingStrategiesDetailed as unknown as RichRenderFn),
  backpropagation: () => import('./backpropagation-detailed').then((m) => m.renderBackpropagationDetailed as unknown as RichRenderFn),
  dropout: () => import('./dropout-detailed').then((m) => m.renderDropoutDetailed as unknown as RichRenderFn),
  'norm-comparison': () => import('./norm-comparison-detailed').then((m) => m.renderNormComparisonDetailed as unknown as RichRenderFn),
  'weight-init': () => import('./weight-init-detailed').then((m) => m.renderWeightInitDetailed as unknown as RichRenderFn),

  // Optimization config-based renderers
  'flash-attention': () => import('./optimization-renderers').then((m) => m.renderFlashAttentionDetailed),
  'kv-cache': () => import('./optimization-renderers').then((m) => m.renderKvCacheDetailed),
  quantization: () => import('./optimization-renderers').then((m) => m.renderQuantizationDetailed),
  'paged-attention': () => import('./optimization-renderers').then((m) => m.renderPagedAttentionDetailed),
  'prefill-decode': () => import('./optimization-renderers').then((m) => m.renderPrefillDecodeDetailed),
  'continuous-batching': () => import('./optimization-renderers').then((m) => m.renderContinuousBatchingDetailed),
  'speculative-decoding': () => import('./optimization-renderers').then((m) => m.renderSpeculativeDecodingDetailed),
  'context-extension': () => import('./optimization-renderers').then((m) => m.renderContextExtensionDetailed),
  'attention-sink': () => import('./optimization-renderers').then((m) => m.renderAttentionSinkDetailed),
  'gradient-checkpointing': () => import('./optimization-renderers').then((m) => m.renderGradientCheckpointingDetailed),
  'mixed-precision': () => import('./optimization-renderers').then((m) => m.renderMixedPrecisionDetailed),
  'memory-calculator': () => import('./optimization-renderers').then((m) => m.renderMemoryCalculatorDetailed),
  'training-performance': () => import('./optimization-renderers').then((m) => m.renderTrainingPerformanceDetailed),
  'config-generator': () => import('./optimization-renderers').then((m) => m.renderConfigGeneratorDetailed),
  scheduling: () => import('./optimization-renderers').then((m) => m.renderSchedulingDetailed),
  'gpu-memory-hierarchy': () => import('./optimization-renderers').then((m) => m.renderGpuMemoryHierarchyDetailed),
  'inference-serving': () => import('./optimization-renderers').then((m) => m.renderInferenceServingDetailed),
  'evaluation-benchmarking': () => import('./optimization-renderers').then((m) => m.renderEvaluationBenchmarkingDetailed),
  'structured-generation': () => import('./optimization-renderers').then((m) => m.renderStructuredGenerationDetailed),
  'pruning-sparsity': () => import('./optimization-renderers').then((m) => m.renderPruningSparsityDetailed),
  'cuda-triton-kernels': () => import('./optimization-renderers').then((m) => m.renderCudaTritonKernelsDetailed),
  'graph-optimization': () => import('./optimization-renderers').then((m) => m.renderGraphOptimizationDetailed),
  'model-formats': () => import('./optimization-renderers').then((m) => m.renderModelFormatsDetailed),

  // Training config-based renderers
  'alignment-training': () => import('./training-parallel-renderers').then((m) => m.renderAlignmentTrainingDetailed),
  'pretrain-pipeline': () => import('./training-parallel-renderers').then((m) => m.renderPretrainPipelineDetailed),
  'sft-training': () => import('./training-parallel-renderers').then((m) => m.renderSftTrainingDetailed),
  lora: () => import('./training-parallel-renderers').then((m) => m.renderLoraDetailed),
  'reward-model': () => import('./training-parallel-renderers').then((m) => m.renderRewardModelDetailed),
  optimizer: () => import('./training-parallel-renderers').then((m) => m.renderOptimizerDetailed),
  'knowledge-distillation': () => import('./training-parallel-renderers').then((m) => m.renderKnowledgeDistillationDetailed),
  'lr-schedule': () => import('./training-parallel-renderers').then((m) => m.renderLrScheduleDetailed),
  'scaling-laws': () => import('./training-parallel-renderers').then((m) => m.renderScalingLawsDetailed),
  'gradient-accumulation': () => import('./training-parallel-renderers').then((m) => m.renderGradientAccumulationDetailed),
  'checkpoint-recovery': () => import('./training-parallel-renderers').then((m) => m.renderCheckpointRecoveryDetailed),

  // Parallelism config-based renderers
  'data-parallel': () => import('./training-parallel-renderers').then((m) => m.renderDataParallelDetailed),
  fsdp: () => import('./training-parallel-renderers').then((m) => m.renderFsdpDetailed),
  'tensor-parallel': () => import('./training-parallel-renderers').then((m) => m.renderTensorParallelDetailed),
  'pipeline-parallel': () => import('./training-parallel-renderers').then((m) => m.renderPipelineParallelDetailed),
  'sequence-parallel': () => import('./training-parallel-renderers').then((m) => m.renderSequenceParallelDetailed),
  'context-parallel': () => import('./training-parallel-renderers').then((m) => m.renderContextParallelDetailed),
  'expert-parallel': () => import('./training-parallel-renderers').then((m) => m.renderExpertParallelDetailed),
  'hybrid-parallel': () => import('./training-parallel-renderers').then((m) => m.renderHybridParallelDetailed),
  'comm-primitives': () => import('./training-parallel-renderers').then((m) => m.renderCommPrimitivesDetailed),
  'network-topology': () => import('./training-parallel-renderers').then((m) => m.renderNetworkTopologyDetailed),
  'distributed-debugging': () => import('./training-parallel-renderers').then((m) => m.renderDistributedDebuggingDetailed),

  // Multimodal renderers
  'audio-encoder': () => import('./audio-encoder-detailed').then((m) => m.renderAudioEncoderDetailed as unknown as RichRenderFn),
  'vit-encoder': () => import('./vit-encoder-detailed').then((m) => m.renderVitEncoderDetailed as unknown as RichRenderFn),
  'vl-fusion': () => import('./vl-fusion-detailed').then((m) => m.renderVlFusionDetailed as unknown as RichRenderFn),
  'dit-architecture': () => import('./dit-architecture-detailed').then((m) => m.renderDiTArchitectureDetailed as unknown as RichRenderFn),

  // Components without dedicated detailed renderers — use generic
  mamba: () => import('./mamba-detailed').then((m) => m.renderMambaDetailed as unknown as RichRenderFn),
  'moe-router': () => import('./moe-router-detailed').then((m) => m.renderMoeRouterDetailed as unknown as RichRenderFn),
  'mixture-of-depths': () =>
    import('./mixture-of-depths-detailed').then((m) => m.renderMixtureOfDepthsDetailed as unknown as RichRenderFn),
  'expert-parallel-detail': () => import('./generic').then((m) => m.renderGenericDetailed),
  'lookahead-decoding': () => import('./generic').then((m) => m.renderGenericDetailed),
  'clip-encoder': () => import('./clip-encoder-detailed').then((m) => m.renderClipEncoderDetailed as unknown as RichRenderFn),
  'diffusion-scheduler': () =>
    import('./diffusion-scheduler-detailed').then((m) => m.renderDiffusionSchedulerDetailed as unknown as RichRenderFn),
  'image-tokenizer': () => import('./image-tokenizer-detailed').then((m) => m.renderImageTokenizerDetailed as unknown as RichRenderFn),
  grpo: () => import('./generic').then((m) => m.renderGenericDetailed),
  'online-dpo': () => import('./generic').then((m) => m.renderGenericDetailed),
  'multi-turn-rlhf': () => import('./generic').then((m) => m.renderGenericDetailed),
  'synthetic-data-pipeline': () => import('./generic').then((m) => m.renderGenericDetailed),
  'tp-overlap': () => import('./generic').then((m) => m.renderGenericDetailed),
  'pp-interleaved': () => import('./generic').then((m) => m.renderGenericDetailed),
  'zero-stage3': () => import('./generic').then((m) => m.renderGenericDetailed),
  'chunked-prefill': () => import('./generic').then((m) => m.renderGenericDetailed),
  'disaggregated-prefill': () => import('./generic').then((m) => m.renderGenericDetailed),
  'prefix-caching': () => import('./generic').then((m) => m.renderGenericDetailed),
  'prefix-tuning': () => import('./generic').then((m) => m.renderGenericDetailed),
  dora: () => import('./generic').then((m) => m.renderGenericDetailed),
  'encoder-decoder-block': () =>
    import('./encoder-decoder-block').then((m) => m.renderEncoderDecoderBlockDetailed as unknown as RichRenderFn),
  'kv-cache-compression': () => import('./generic').then((m) => m.renderGenericDetailed),
  'kv-cache-quantization': () => import('./generic').then((m) => m.renderGenericDetailed),
  'contrastive-loss': () =>
    import('./contrastive-loss-detailed').then((m) => m.renderContrastiveLossDetailed as unknown as RichRenderFn),
};

export function hasRichRenderer(id: string): boolean {
  return id in richLoaders;
}

export async function loadRichRenderer(id: string): Promise<RichRenderFn> {
  const loader = richLoaders[id];
  if (loader) return loader();
  throw new Error(`No rich renderer for ${id}`);
}

// Keep these for backward compatibility with any code still using them
export function hasDetailedRenderer(id: string): boolean {
  return id in richLoaders;
}

export async function loadDetailedRenderer(id: string): Promise<RenderFn> {
  const loader = richLoaders[id];
  if (loader) return loader() as unknown as Promise<RenderFn>;
  const generic = await import('./generic');
  return generic.renderGenericDetailed;
}

export { EmbeddingStep, ResidualStep };
